import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Loader2, Globe2, Sparkles, Home, Wifi, Plane, AlertCircle } from "lucide-react";
import { trackEvent, attributionForSubmission } from "../lib/analytics";
import { isValidPhoneNumber, countryOf, countryNameOf, iso2OfCountryName, callingCodeOf } from "../lib/phone";
import { parsePhoneNumber as _parsePhoneForLocal } from "libphonenumber-js";
import PhoneInputE164 from "./PhoneInputE164";
import SearchableSelect from "./SearchableSelect";
import MultiSelectChips from "./MultiSelectChips";
import { useMasterList } from "../lib/useMasterList";
import {
    PREFERRED_DAYS, PREFERRED_TIME_WINDOWS, CLASS_DURATIONS, LEARNING_LEVELS,
    leadCompleteness,
} from "../lib/aiMatchFields";
import DataCompletenessMeter from "./DataCompletenessMeter";
import ForeignDemoTimePicker from "./ForeignDemoTimePicker";
import ForeignCurriculumSelector from "./ForeignCurriculumSelector";
import { convertTime, to12Hour, defaultTimezoneForCountry, COUNTRY_TIMEZONES } from "../lib/timezones";
import { resolveStateFromCity } from "../data/indianCityToState";

// V26 — Book Free Demo Class form with strict per-field validation + dynamic
// behaviour driven by Tuition Mode. See feature spec items 1-8.
const MODE_HOME = "Home Tuition";
const MODE_PAN_INDIA = "Live Online (Pan India)";
const MODE_FOREIGN = "Live Online (Foreign / NRI)";

// Home Tuition is currently offered ONLY in these three cities.
const HOME_TUITION_CITIES = ["Jabalpur", "Bhopal", "Nagpur"];

// V47 — Home Tuition: city → state auto-mapping. Backend is authoritative,
// but we send this hidden value too so the payload is self-consistent.
const HOME_TUITION_CITY_STATE = {
    Jabalpur: "Madhya Pradesh",
    Bhopal: "Madhya Pradesh",
    Nagpur: "Maharashtra",
};

const MODE_OPTIONS = [
    { value: MODE_HOME, label: "Home Tuition", icon: Home,
      hint: "In-person at student's home (Jabalpur / Bhopal / Nagpur only)." },
    { value: MODE_PAN_INDIA, label: "Live Online (Pan India)", icon: Wifi,
      hint: "1:1 live online classes anywhere in India." },
    { value: MODE_FOREIGN, label: "Live Online (Foreign / NRI)", icon: Plane,
      hint: "1:1 live online classes for students outside India." },
];

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// V178 — Seed Foreign/NRI demo time from the public Time Converter link (no-op when absent).
function applyDemoPrefill(base, p) {
    if (!p) return base;
    const entry = p.tz ? COUNTRY_TIMEZONES.find((c) => c.tz === p.tz) : null;
    const next = {
        ...base, mode: MODE_FOREIGN, country: "",
        preferred_demo_date: p.date, preferred_demo_time: p.time,
    };
    if (entry) {
        next.preferred_demo_tz = entry.tz;
        next.preferred_demo_country_code = entry.code;
        next.preferred_demo_country_name = entry.name;
        const name = countryNameOf(entry.code);
        if (name) { next.country = name; next._country_autofill = name; }
        const ist = convertTime(p.date, p.time, entry.tz, "Asia/Kolkata");
        next.preferred_demo_local_12h = to12Hour(p.time);
        next.preferred_demo_ist_formatted = ist?.formatted || "";
        next.preferred_demo_ist_iso = ist?.iso || "";
    }
    return next;
}

export default function RegistrationForm({ defaultCity = "", title = "Book a Free Demo Class", preferCountry, demoPrefill = null }) {
    const { items: countryItems } = useMasterList("country");
    const { items: languageItems } = useMasterList("language");
    const { items: examItems } = useMasterList("exam_category");
    const { items: currItems } = useMasterList("curriculum_type");

    const [form, setForm] = useState(() => applyDemoPrefill({
        name: "", parent_name: "", email: "", phone: "",
        country: "India", state: "", city: defaultCity, grade: "", board: "",
        school_name: "", subjects: "",
        mode: "",  // empty by default — user MUST pick (spec #3)
        message: "",
        // V37 — Home Tuition only (mandatory when mode === Home Tuition)
        permanent_address: "",
        pin_code: "",
        // V51 — Foreign / NRI demo time + IST conversion (Foreign mode only)
        preferred_demo_date: "",
        preferred_demo_time: "",
        preferred_demo_local_12h: "",
        preferred_demo_tz: "",
        preferred_demo_country_code: "",
        preferred_demo_country_name: "",
        preferred_demo_ist_formatted: "",
        preferred_demo_ist_iso: "",
        // V52 — Foreign / NRI country→curriculum→board cascade (Foreign mode only)
        foreign_country_code: "",
        curriculum_id: "",
        curriculum: "",
        board_id: "",
        // V24 — Optional AI fields
        preferred_tutor_gender: "",
        preferred_time_window: "",
        class_duration_minutes: "",
        exam_goal: "",
        curriculum_preference: "",
        learning_level: "",
        special_requirements: "",
    }, demoPrefill));
    const [languagePreference, setLanguagePreference] = useState([]);
    const [preferredDays, setPreferredDays] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Field-level error state. Keys mirror the form keys.
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
    };

    // ── Dynamic behaviour driven by `mode` (spec #4-#7) ──────────────────
    const mode = form.mode;
    const isHome = mode === MODE_HOME;
    const isPanIndia = mode === MODE_PAN_INDIA;
    const isForeign = mode === MODE_FOREIGN;
    const isIndianMode = isHome || isPanIndia;

    // Mode change handler — normalises country/city + clears stale errors.
    const changeMode = (newMode) => {
        setForm((f) => {
            const next = { ...f, mode: newMode };
            if (newMode === MODE_HOME || newMode === MODE_PAN_INDIA) {
                next.country = "India";
                if (newMode === MODE_HOME && next.city && !HOME_TUITION_CITIES.includes(next.city)) {
                    next.city = "";
                }
            } else if (newMode === MODE_FOREIGN) {
                if ((next.country || "").toLowerCase() === "india") next.country = "";
            }
            // V37 — Permanent Address + Pincode are only relevant in Home Tuition.
            // Wipe them when the parent switches to any online mode so stale values
            // never reach the backend or the lead detail view.
            if (newMode !== MODE_HOME) {
                next.permanent_address = "";
                next.pin_code = "";
            }
            // V47 + V126 — Auto-set state from city for Indian modes
            // (Home Tuition + Pan India). Foreign / NRI never populates an
            // Indian state.
            if (newMode === MODE_HOME || newMode === MODE_PAN_INDIA) {
                next.state = resolveStateFromCity(next.city);
            } else {
                next.state = "";
            }
            // V51 — Foreign demo time fields are only meaningful in Foreign/NRI mode
            if (newMode !== MODE_FOREIGN) {
                next.preferred_demo_date = "";
                next.preferred_demo_time = "";
                next.preferred_demo_local_12h = "";
                next.preferred_demo_tz = "";
                next.preferred_demo_country_code = "";
                next.preferred_demo_country_name = "";
                next.preferred_demo_ist_formatted = "";
                next.preferred_demo_ist_iso = "";
                // V52 — also wipe the foreign curriculum cascade keys
                next.foreign_country_code = "";
                next.curriculum_id = "";
                next.curriculum = "";
                next.board_id = "";
            }
            return next;
        });
        setErrors((er) => {
            const next = { ...er };
            delete next.mode;
            if (newMode === MODE_HOME || newMode === MODE_PAN_INDIA) delete next.country;
            if (newMode === MODE_FOREIGN) delete next.city;
            if (newMode !== MODE_HOME) {
                delete next.permanent_address;
                delete next.pin_code;
            }
            return next;
        });
    };

    // V24 — Live completeness % for the AI meter
    const completenessPct = useMemo(() => leadCompleteness({
        ...form,
        subjects: form.subjects,
        language_preference: languagePreference,
        preferred_days: preferredDays,
    }), [form, languagePreference, preferredDays]);

    // V128 — Helper used by both `onPhoneChange` and `onPhoneCountryChange`
    // to seed the "Preferred Demo Time · Country & Timezone" dropdown when
    // the parent auto-fills the phone country. Mutates `next` in place.
    //
    // Contract:
    //   • Only fires for Foreign / NRI mode.
    //   • Only seeds when the current TZ is empty OR was previously seeded
    //     by this helper (`_tz_autofill` marker). Any manual TZ pick is
    //     respected forever — even if the parent later changes flag.
    //   • Never seeds India (that's an Indian-mode concern, not this widget).
    //   • Skips when we don't have a COUNTRY_TIMEZONES entry for the ISO —
    //     leaves the dropdown empty so the parent picks manually.
    const _maybeSeedTimezone = (next, prev, iso) => {
        if (!iso) return;
        if (iso.toUpperCase() === "IN") return;
        const curTz = (prev.preferred_demo_tz || "").trim();
        const wasSeeded = !curTz || curTz === prev._tz_autofill;
        if (!wasSeeded) return;
        const entry = defaultTimezoneForCountry(iso);
        if (!entry) return;
        next.preferred_demo_tz = entry.tz;
        next.preferred_demo_country_code = entry.code;
        next.preferred_demo_country_name = entry.name;
        next._tz_autofill = entry.tz;
        // If the parent already picked a date + time, live-recompute the IST
        // preview so the blue banner reflects the new timezone immediately.
        const d = next.preferred_demo_date;
        const t = next.preferred_demo_time;
        if (d && t) {
            const ist = convertTime(d, t, entry.tz, "Asia/Kolkata");
            next.preferred_demo_local_12h = to12Hour(t);
            next.preferred_demo_ist_formatted = ist?.formatted || "";
            next.preferred_demo_ist_iso = ist?.iso || "";
        }
    };

    // Phone-country flag → country auto-sync (only for Foreign/NRI mode).
    const onPhoneChange = (v) => {
        setForm((f) => {
            const next = { ...f, phone: v };
            // V44 — Foreign/NRI mode only: auto-fill Country from phone's ISO-2
            // unless the user has typed a custom country we don't recognise.
            if (f.mode === MODE_FOREIGN && v) {
                const iso = countryOf(v);
                const name = countryNameOf(iso);
                if (name && name.toLowerCase() !== "india") {
                    // Only overwrite when (a) field is empty, or (b) current
                    // value is the previously-auto-filled name. We treat a
                    // manually-typed custom country as "leave alone".
                    const cur = (f.country || "").trim();
                    const wasAutofilled = !cur || cur === f._country_autofill;
                    if (wasAutofilled) {
                        next.country = name;
                        next._country_autofill = name;
                    }
                }
                // V128 — Also seed the "Preferred Demo Time" country & timezone
                // dropdown when the parent hasn't manually picked one yet.
                _maybeSeedTimezone(next, f, iso);
                // V130 — Track the phone-country ISO so the curriculum
                // selector below can auto-seed its "Country of Study" +
                // curriculum defaults from it (respect-manual-override
                // enforced inside the selector itself).
                if (iso) next._phone_country_iso = iso.toUpperCase();
            }
            return next;
        });
        if (errors.phone) setErrors((er) => ({ ...er, phone: null }));
        if (errors.country) setErrors((er) => ({ ...er, country: null }));
    };

    // V44 — Foreign/NRI: when the parent picks a flag in the phone widget,
    // sync the Country field too. Same auto-fill heuristic as above.
    const onPhoneCountryChange = (iso) => {
        if (!isForeign || !iso) return;
        const name = countryNameOf(iso);
        if (!name || name.toLowerCase() === "india") return;
        setForm((f) => {
            const cur = (f.country || "").trim();
            const wasAutofilled = !cur || cur === f._country_autofill;
            const next = { ...f };
            if (wasAutofilled) {
                next.country = name;
                next._country_autofill = name;
            }
            // V128 — Also try to seed the timezone dropdown, independent of
            // whether the country field itself was overwritten (a returning
            // parent may have picked a valid country manually but never
            // opened the TZ dropdown).
            _maybeSeedTimezone(next, f, iso);
            // V130 — Same phone-country ISO forwarded to the curriculum selector.
            next._phone_country_iso = iso.toUpperCase();
            return next;
        });
        if (errors.country) setErrors((er) => ({ ...er, country: null }));
    };

    // V44 — When the parent changes the Country combobox to a recognisable
    // country, push the matching calling-code prefix down to the phone widget
    // so the flag and dial-code update. We rewrite the phone value's calling
    // code rather than relying on `defaultCountry` (which only seeds initial
    // render in react-phone-number-input). Custom typed countries (no ISO-2
    // match) leave the phone widget untouched per spec rule #4.
    const onCountryChange = (e) => {
        const newName = e.target.value;
        const iso = iso2OfCountryName(newName);
        setForm((f) => {
            const next = { ...f, country: newName, _country_autofill: undefined };
            if (iso) {
                const prefix = callingCodeOf(iso);
                if (prefix) {
                    // Try to preserve the user's local digits. Parse the
                    // existing phone, extract the national number, and
                    // reattach it under the new country code.
                    let local = "";
                    try {
                        const p = _parsePhoneForLocal(f.phone);
                        if (p?.nationalNumber) local = String(p.nationalNumber);
                    } catch { /* ignore */ }
                    next.phone = `${prefix}${local}`;
                }
            }
            return next;
        });
        if (errors.country) setErrors((er) => ({ ...er, country: null }));
        if (errors.phone) setErrors((er) => ({ ...er, phone: null }));
    };
    const forcedPhoneCountry = useMemo(() => {
        if (!isForeign) return null;
        return iso2OfCountryName(form.country) || null;
    }, [isForeign, form.country]);

    // ── Validate the entire form, return { errors, ok } ──────────────────
    const validate = () => {
        const e = {};
        if (!form.mode) e.mode = "Tuition Mode is mandatory.";
        if (!form.name.trim()) e.name = "Student Name is mandatory.";
        if (!form.parent_name.trim()) e.parent_name = "Parent Name is mandatory.";
        if (!form.email.trim()) e.email = "Email is mandatory.";
        else if (!EMAIL_RX.test(form.email.trim())) e.email = "Please enter a valid email address.";
        if (!form.phone.trim()) e.phone = "Phone Number is mandatory.";
        else if (!isValidPhoneNumber(form.phone)) e.phone = "Please enter a valid phone number.";
        if (!form.grade) e.grade = "Grade is mandatory.";
        if (!form.board) e.board = "Board is mandatory.";
        if (!form.school_name.trim()) e.school_name = "School Name is mandatory.";
        if (!form.subjects.trim()) e.subjects = "Subjects are mandatory.";

        // Mode-dependent country / city rules
        if (form.mode === MODE_FOREIGN) {
            if (!form.country.trim()) e.country = "Country is mandatory.";
            else if (form.country.trim().toLowerCase() === "india") {
                e.country = "Select a non-India country for Foreign / NRI mode.";
            }
            // V51 — Foreign/NRI demo time is mandatory (date + time + country tz)
            if (!form.preferred_demo_date) e.preferred_demo_date = "Please select a preferred date.";
            if (!form.preferred_demo_time) e.preferred_demo_time = "Please select your local time.";
            if (!form.preferred_demo_tz) e.preferred_demo_tz = "Please select your country / timezone.";
            // city is optional in Foreign/NRI
        } else if (form.mode === MODE_HOME) {
            if (!form.city.trim()) e.city = "City is mandatory.";
            else if (!HOME_TUITION_CITIES.includes(form.city)) {
                e.city = "Home Tuition is available only in Jabalpur, Bhopal or Nagpur.";
            }
            // V37 — Permanent Address + 6-digit Pincode required for Home Tuition only.
            if (!form.permanent_address.trim()) {
                e.permanent_address = "Student Permanent Address is mandatory for Home Tuition.";
            }
            const pin = (form.pin_code || "").trim();
            if (!pin) e.pin_code = "Pincode is mandatory for Home Tuition.";
            else if (!/^\d{6}$/.test(pin)) e.pin_code = "Pincode must be exactly 6 digits.";
        } else if (form.mode === MODE_PAN_INDIA) {
            if (!form.city.trim()) e.city = "City is mandatory.";
        }
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        const eMap = validate();
        if (!agreed) {
            // We don't block individual field errors on this — render policy error inline only.
            eMap._agreed = "Please accept the Parent Policy & Terms to continue.";
        }
        setErrors(eMap);
        if (Object.keys(eMap).length > 0) {
            // Scroll the first error into view for accessibility.
            const firstKey = Object.keys(eMap).find((k) => k !== "_agreed") || "_agreed";
            const el = document.querySelector(`[data-field-error-anchor="${firstKey}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.error("Please fix the highlighted fields and try again.");
            return;
        }
        setSubmitting(true);
        try {
            const attribution = attributionForSubmission();
            const payload = {
                ...form,
                country: isIndianMode ? "India" : form.country,
                class_duration_minutes: form.class_duration_minutes ? Number(form.class_duration_minutes) : null,
                language_preference: languagePreference.length ? languagePreference : null,
                preferred_days: preferredDays.length ? preferredDays : null,
                preferred_tutor_gender: form.preferred_tutor_gender || null,
                preferred_time_window: form.preferred_time_window || null,
                exam_goal: form.exam_goal || null,
                curriculum_preference: form.curriculum_preference || null,
                learning_level: form.learning_level || null,
                special_requirements: form.special_requirements || null,
                phone_country: countryOf(form.phone) || null,
                message: `${form.message || ""}${form.message ? "\n\n" : ""}[Parent Policy accepted at ${new Date().toISOString()}]`,
                attribution,
            };
            const { data } = await api.post("/registration", payload);
            trackEvent("free_demo_requested", {
                registration_id: data?.id,
                form_country: payload.country || null,
                form_city: payload.city || null,
                form_grade: payload.grade || null,
                form_mode: payload.mode || null,
                ai_completeness: completenessPct,
            });
            toast.success(data.message || "Demo class request received.");
            setForm({
                name: "", parent_name: "", email: "", phone: "",
                country: "India", state: "", city: "", grade: "", board: "",
                school_name: "", subjects: "",
                mode: "",
                message: "",
                permanent_address: "",
                pin_code: "",
                preferred_demo_date: "", preferred_demo_time: "", preferred_demo_local_12h: "",
                preferred_demo_tz: "", preferred_demo_country_code: "", preferred_demo_country_name: "",
                preferred_demo_ist_formatted: "", preferred_demo_ist_iso: "",
                foreign_country_code: "", curriculum_id: "", curriculum: "", board_id: "",
                preferred_tutor_gender: "", preferred_time_window: "",
                class_duration_minutes: "", exam_goal: "",
                curriculum_preference: "", learning_level: "",
                special_requirements: "",
            });
            setLanguagePreference([]); setPreferredDays([]); setAgreed(false); setErrors({});
        } catch (err) {
            // Map backend "Missing mandatory fields: a, b" → per-field errors
            const detail = err.response?.data?.detail;
            if (typeof detail === "string" && detail.startsWith("Missing mandatory fields:")) {
                const list = detail.replace("Missing mandatory fields:", "").split(",").map((s) => s.trim()).filter(Boolean);
                const map = {};
                list.forEach((k) => { map[k] = `${humanLabel(k)} is mandatory.`; });
                setErrors(map);
            }
            toast.error(formatApiError(detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} noValidate className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-4 shadow-lg" data-testid="registration-form">
            <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0F172A]">{title}</h3>
            <p className="text-sm text-slate-500">Get a 1:1 free demo with a verified tutor — no payment required.</p>

            <DataCompletenessMeter percent={completenessPct} kind="lead" testid="reg-ai-meter" />

            {/* ── 1. Tuition Mode FIRST (spec #3) ─────────────────────── */}
            <div className="space-y-2" data-field-error-anchor="mode">
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block">
                    Tuition Mode <span className="text-red-600">*</span>
                </span>
                <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" data-testid="reg-mode-radios">
                    {MODE_OPTIONS.map((opt) => {
                        const active = form.mode === opt.value;
                        const Icon = opt.icon;
                        return (
                            <label
                                key={opt.value}
                                className={`flex flex-col gap-1 p-3 rounded-xl border-2 cursor-pointer transition
                                    ${active
                                        ? "border-[#1E3A8A] bg-blue-50 shadow-sm"
                                        : "border-slate-200 hover:border-slate-300 bg-white"}`}
                                data-testid={`reg-mode-${opt.value === MODE_HOME ? "home" : opt.value === MODE_PAN_INDIA ? "pan-india" : "foreign"}`}
                            >
                                <input
                                    type="radio"
                                    name="mode"
                                    className="sr-only"
                                    value={opt.value}
                                    checked={active}
                                    onChange={() => changeMode(opt.value)}
                                    data-testid={`reg-mode-${opt.value === MODE_HOME ? "home" : opt.value === MODE_PAN_INDIA ? "pan-india" : "foreign"}-input`}
                                />
                                <span className="inline-flex items-center gap-2 font-semibold text-sm text-[#0F172A]">
                                    <Icon className={`w-4 h-4 ${active ? "text-[#1E3A8A]" : "text-slate-400"}`} />
                                    {opt.label}
                                </span>
                                <span className="text-[11px] text-slate-600 leading-snug">{opt.hint}</span>
                            </label>
                        );
                    })}
                </div>
                <FieldError msg={errors.mode} testid="reg-error-mode" />
                {isForeign && (
                    <p className="text-[12px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="reg-intl-notice">
                        <Globe2 className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
                        <span>
                            Anand Tutorials currently provides only <strong>1:1 Live Online Classes</strong> for international students.
                        </span>
                    </p>
                )}
                {isForeign && demoPrefill && (
                    <p className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="reg-demo-prefill-notice">
                        Your preferred demo time was filled in from the Time Converter
                        {!form.preferred_demo_tz ? " — please select your country / time zone below" : ""}.
                        Please complete the remaining details. This is a requested time only; our team will confirm the demo class.
                    </p>
                )}
            </div>

            {/* ── Identity ─────────────────────────────────────────────── */}
            <div className="grid sm:grid-cols-2 gap-4">
                <F label="Student Name *" error={errors.name}>
                    <input type="text" value={form.name} onChange={update("name")} data-testid="reg-input-name" className={cls(errors.name)} />
                </F>
                <F label="Parent Name *" error={errors.parent_name}>
                    <input type="text" value={form.parent_name} onChange={update("parent_name")} data-testid="reg-input-parent" className={cls(errors.parent_name)} />
                </F>
                <F label="Email *" error={errors.email}>
                    <input type="email" value={form.email} onChange={update("email")} data-testid="reg-input-email" className={cls(errors.email)} />
                </F>
                <F label="Phone *" error={errors.phone}>
                    <PhoneInputE164
                        key={isForeign ? `pi-${forcedPhoneCountry || "auto"}` : "pi-in"}
                        value={form.phone}
                        onChange={onPhoneChange}
                        onCountryChange={onPhoneCountryChange}
                        forceCountry={isForeign ? forcedPhoneCountry : null}
                        preferCountry={isForeign ? (forcedPhoneCountry || null) : (preferCountry || "in")}
                        testid="reg-input-phone"
                    />
                </F>

                {/* Country — hidden for Indian modes (auto India). For Foreign/NRI
                    we use a NATIVE combobox (<input list>) so parents can either pick
                    a country from the master list OR type any free-text country we
                    don't yet support (V38). Backend already accepts any non-India
                    country string without master-list restriction. */}
                {isForeign && (
                    <F label="Country *" hint="(pick from list, type your country, or auto-fills from phone code)" error={errors.country}>
                        <input
                            type="text"
                            list="reg-country-options"
                            value={form.country}
                            onChange={onCountryChange}
                            data-testid="reg-input-country"
                            className={cls(errors.country)}
                            placeholder="Start typing — e.g. USA, UAE, Australia, Tonga…"
                            autoComplete="country"
                            spellCheck={false}
                            maxLength={80}
                        />
                        <datalist id="reg-country-options">
                            {countryItems
                                .filter((c) => (c.value || "").toLowerCase() !== "india")
                                .map((c) => (
                                    <option key={c.value} value={c.value} />
                                ))}
                        </datalist>
                        {/* V127 — Green auto-detected hint mirroring the city→state UX.
                            Only shown when the current country value came from the
                            phone-code auto-fill (not a manual override). */}
                        {form._country_autofill && form.country && form.country === form._country_autofill && (
                            <p
                                className="mt-1.5 text-[11px] text-emerald-700"
                                data-testid="reg-country-auto-hint"
                            >
                                Country auto-detected from phone code: <strong>{form.country}</strong>
                            </p>
                        )}
                    </F>
                )}
                {isIndianMode && (
                    <F label="Country" hint="(auto-set for India-based modes)">
                        <input
                            type="text"
                            value="India"
                            readOnly
                            data-testid="reg-input-country-readonly"
                            className={`${cls(null)} bg-slate-50 text-slate-500 cursor-not-allowed`}
                        />
                    </F>
                )}

                {/* City — varies by mode */}
                {isHome && (
                    <F label="City *" hint="(Home Tuition cities only)" error={errors.city}>
                        <SearchableSelect
                            options={HOME_TUITION_CITIES.map((c) => ({ value: c, label: c }))}
                            value={form.city}
                            onChange={(v) => {
                                setForm((f) => ({
                                    ...f,
                                    city: v,
                                    // V47 + V126 — Auto-fetch state from city
                                    // via the shared india-wide map so Home
                                    // and Pan-India share one source of truth.
                                    state: resolveStateFromCity(v),
                                }));
                                if (errors.city) setErrors((er) => ({ ...er, city: null }));
                            }}
                            placeholder="Search: Jabalpur / Bhopal / Nagpur"
                            testid="reg-input-city-home"
                            allowClear
                        />
                    </F>
                )}
                {isPanIndia && (
                    <F label="City *" hint="(any Indian city — state auto-detected)" error={errors.city}>
                        <input
                            type="text"
                            value={form.city}
                            onChange={(e) => {
                                const v = e.target.value;
                                setForm((f) => ({
                                    ...f,
                                    city: v,
                                    // V126 — Auto-fill state as the parent types
                                    // (only when we have a confident match).
                                    // Unresolved cities keep state="" so admin
                                    // can pick manually rather than guess.
                                    state: resolveStateFromCity(v),
                                }));
                                if (errors.city) setErrors((er) => ({ ...er, city: null }));
                            }}
                            data-testid="reg-input-city-pan-india"
                            className={cls(errors.city)}
                            placeholder="Enter your city (e.g. Mumbai)"
                        />
                        {form.state && (
                            <p className="mt-1.5 text-[11px] text-emerald-700" data-testid="reg-state-auto-hint">
                                State auto-detected: <strong>{form.state}</strong>
                            </p>
                        )}
                    </F>
                )}
                {isForeign && (
                    <F label="City (optional)">
                        <input
                            type="text"
                            value={form.city}
                            onChange={update("city")}
                            data-testid="reg-input-city-foreign"
                            className={cls(null)}
                            placeholder="Optional — your city / town"
                        />
                    </F>
                )}
                {isForeign && (
                    <div className="md:col-span-2">
                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
                            Preferred Demo Time (Student&apos;s Local Time) *
                        </div>
                        <ForeignDemoTimePicker
                            autoFilled={!!form._tz_autofill && form._tz_autofill === form.preferred_demo_tz}
                            value={{
                                date: form.preferred_demo_date || "",
                                time24: form.preferred_demo_time || "",
                                tz: form.preferred_demo_tz || "",
                                country_code: form.preferred_demo_country_code || "",
                                country_name: form.preferred_demo_country_name || "",
                            }}
                            onChange={(patch) => setForm((f) => {
                                const next = { ...f };
                                if ("date" in patch) next.preferred_demo_date = patch.date;
                                if ("time24" in patch) next.preferred_demo_time = patch.time24;
                                if ("tz" in patch) next.preferred_demo_tz = patch.tz;
                                if ("country_code" in patch) next.preferred_demo_country_code = patch.country_code;
                                if ("country_name" in patch) next.preferred_demo_country_name = patch.country_name;
                                // V128 — When the parent manually picks a
                                // country/timezone from THIS dropdown, clear
                                // the auto-fill marker so a later phone-flag
                                // change never overwrites their explicit choice.
                                if ("tz" in patch || "country_code" in patch) {
                                    next._tz_autofill = undefined;
                                }
                                // Live IST denormalisation — recompute so it ships in the payload
                                const date = next.preferred_demo_date;
                                const time24 = next.preferred_demo_time;
                                const tz = next.preferred_demo_tz;
                                if (date && time24 && tz) {
                                    const ist = convertTime(date, time24, tz, "Asia/Kolkata");
                                    next.preferred_demo_local_12h = to12Hour(time24);
                                    next.preferred_demo_ist_formatted = ist?.formatted || "";
                                    next.preferred_demo_ist_iso = ist?.iso || "";
                                } else {
                                    next.preferred_demo_local_12h = "";
                                    next.preferred_demo_ist_formatted = "";
                                    next.preferred_demo_ist_iso = "";
                                }
                                return next;
                            })}
                        />
                    </div>
                )}
                {!form.mode && (
                    <F label="City">
                        <input
                            type="text"
                            value=""
                            disabled
                            data-testid="reg-input-city-disabled"
                            className={`${cls(null)} bg-slate-50 text-slate-400 cursor-not-allowed`}
                            placeholder="Pick a Tuition Mode first…"
                        />
                    </F>
                )}

                <F label="Grade / Class *" error={errors.grade}>
                    <select value={form.grade} onChange={update("grade")} data-testid="reg-input-grade" className={cls(errors.grade)}>
                        <option value="">Select grade…</option>
                        <option>Nursery / KG</option>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => <option key={n}>{`Class ${n}`}</option>)}
                        <option>College / Engineering</option>
                        <option>NEET</option>
                        <option>JEE</option>
                        <option>Other</option>
                    </select>
                </F>
                <F label="Board *" error={errors.board}>
                    {isForeign ? (
                        <div className="md:col-span-2 -mt-1">
                            <ForeignCurriculumSelector
                                phoneCountryHint={form._phone_country_iso || ""}
                                value={{
                                    country_code: form.foreign_country_code || "",
                                    curriculum_id: form.curriculum_id || "",
                                    board_id: form.board_id || "",
                                    subjects: form.subjects || "",
                                }}
                                onChange={(patch) => setForm((f) => {
                                    const next = { ...f };
                                    if ("country_code" in patch) next.foreign_country_code = patch.country_code;
                                    if ("country_name" in patch) next.country = patch.country_name || next.country;
                                    if ("curriculum_id" in patch) next.curriculum_id = patch.curriculum_id;
                                    if ("curriculum_label" in patch) next.curriculum = patch.curriculum_label;
                                    if ("board_id" in patch) next.board_id = patch.board_id;
                                    if ("board_label" in patch) next.board = patch.board_label;
                                    if ("subjects" in patch) next.subjects = patch.subjects;
                                    return next;
                                })}
                            />
                        </div>
                    ) : (
                        <select value={form.board} onChange={update("board")} data-testid="reg-input-board" className={cls(errors.board)}>
                            <option value="">Select board…</option>
                            <option>CBSE</option><option>ICSE</option><option>IGCSE</option><option>IB</option>
                            <option>A-Level</option><option>AP</option><option>MP Board</option>
                            <option>Maharashtra Board</option><option>NIOS</option><option>Other</option>
                        </select>
                    )}
                </F>
                <F label="School Name *" error={errors.school_name}>
                    <input
                        type="text"
                        value={form.school_name}
                        onChange={update("school_name")}
                        data-testid="reg-input-school"
                        className={cls(errors.school_name)}
                        placeholder="Student's current school"
                    />
                </F>
                <F label="Subjects *" error={errors.subjects}>
                    <input
                        type="text"
                        value={form.subjects}
                        onChange={update("subjects")}
                        data-testid="reg-input-subjects"
                        className={cls(errors.subjects)}
                        placeholder="e.g. Math, Physics, Chemistry"
                    />
                </F>
            </div>

            {/* V37 — Home Tuition only: Permanent Address + 6-digit Pincode for
                accurate nearby-tutor allocation. Hidden for online modes. */}
            {isHome && (
                <div
                    className="rounded-xl border border-pink-200 bg-pink-50/40 p-4 sm:p-5 space-y-4"
                    data-testid="reg-home-address-block"
                >
                    <div className="flex items-start gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-pink-700 bg-pink-100 border border-pink-200 px-2 py-0.5 rounded">
                            Home Tuition
                        </span>
                        <p className="text-[12px] text-slate-700 leading-snug">
                            Required so we can match a verified tutor near the student's home.
                        </p>
                    </div>
                    <F label="Student Permanent Address *" error={errors.permanent_address}>
                        <textarea
                            value={form.permanent_address}
                            onChange={update("permanent_address")}
                            rows={3}
                            maxLength={500}
                            data-testid="reg-input-permanent-address"
                            className={`${cls(errors.permanent_address)} resize-none`}
                            placeholder="Enter complete permanent address"
                        />
                    </F>
                    <F label="Pincode *" hint="(6 digits)" error={errors.pin_code}>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="postal-code"
                            value={form.pin_code}
                            onChange={(e) => {
                                // Allow only digits, cap at 6 chars — UX hardening on top of validation.
                                const v = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                                setForm((f) => ({ ...f, pin_code: v }));
                                if (errors.pin_code) setErrors((er) => ({ ...er, pin_code: null }));
                            }}
                            maxLength={6}
                            pattern="\d{6}"
                            data-testid="reg-input-pin-code"
                            className={cls(errors.pin_code)}
                            placeholder="e.g. 482001"
                        />
                    </F>
                </div>
            )}

            <F label="Message (optional)">
                <textarea value={form.message} onChange={update("message")} rows={3} data-testid="reg-input-message" className={`${cls(null)} resize-none`} placeholder="Preferred schedule, special requests…" />
            </F>

            {/* V24 — Optional AI-first fields */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden" data-testid="reg-ai-section">
                <button
                    type="button"
                    onClick={() => setAiOpen((v) => !v)}
                    data-testid="reg-ai-toggle"
                    aria-expanded={aiOpen}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-emerald-100/40"
                >
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        Help AI find your perfect tutor <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded">Optional — improves match accuracy</span>
                    </span>
                    <span className="text-emerald-700 text-xs">{aiOpen ? "Hide" : "Show"}</span>
                </button>
                {aiOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-4" data-testid="reg-ai-fields">
                        <p className="text-[11px] text-emerald-800 -mt-1">
                            Optional — helps improve AI tutor matching accuracy. Skip what you don&apos;t know and submit anyway.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <F label="Preferred Tutor Gender (optional)">
                                <select value={form.preferred_tutor_gender} onChange={update("preferred_tutor_gender")} data-testid="reg-input-gender-pref" className={cls(null)}>
                                    <option value="">Any</option>
                                    <option value="female">Female</option>
                                    <option value="male">Male</option>
                                </select>
                            </F>
                            <F label="Learning Level (optional)">
                                <select value={form.learning_level} onChange={update("learning_level")} data-testid="reg-input-learning-level" className={cls(null)}>
                                    <option value="">Not specified</option>
                                    {LEARNING_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                                </select>
                            </F>
                            <F label="Exam Goal (optional)">
                                <SearchableSelect
                                    options={examItems.map((e) => ({ value: e.value }))}
                                    value={form.exam_goal}
                                    onChange={(v) => setForm((f) => ({ ...f, exam_goal: v }))}
                                    placeholder="Boards, JEE, NEET, Olympiad…"
                                    testid="reg-input-exam-goal"
                                    allowClear
                                />
                            </F>
                            <F label="Curriculum Preference (optional)">
                                <SearchableSelect
                                    options={currItems.map((c) => ({ value: c.value }))}
                                    value={form.curriculum_preference}
                                    onChange={(v) => setForm((f) => ({ ...f, curriculum_preference: v }))}
                                    placeholder="NCERT-aligned, IB-DP, Cambridge…"
                                    testid="reg-input-curriculum"
                                    allowClear
                                />
                            </F>
                            <F label="Preferred Time Window (optional)">
                                <select value={form.preferred_time_window} onChange={update("preferred_time_window")} data-testid="reg-input-time-window" className={cls(null)}>
                                    <option value="">Not specified</option>
                                    {PREFERRED_TIME_WINDOWS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </F>
                            <F label="Class Duration (optional)">
                                <select value={form.class_duration_minutes} onChange={update("class_duration_minutes")} data-testid="reg-input-duration" className={cls(null)}>
                                    <option value="">Not specified</option>
                                    {CLASS_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.value} minutes</option>)}
                                </select>
                            </F>
                        </div>
                        <F label="Preferred Language(s) (optional — for instruction)">
                            <MultiSelectChips
                                options={languageItems.map((l) => ({ value: l.value }))}
                                value={languagePreference}
                                onChange={setLanguagePreference}
                                placeholder="Search languages…"
                                testid="reg-input-language"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Preferred Days (optional)">
                            <MultiSelectChips
                                options={PREFERRED_DAYS}
                                value={preferredDays}
                                onChange={setPreferredDays}
                                placeholder="Search days…"
                                testid="reg-input-days"
                                pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                            />
                        </F>
                        <F label="Special Requirements (optional)">
                            <textarea
                                value={form.special_requirements}
                                onChange={update("special_requirements")}
                                rows={2}
                                data-testid="reg-input-special-requirements"
                                className={`${cls(null)} resize-none`}
                                placeholder="e.g. weak in algebra, prefers patient tutor, needs reinforcement before unit tests…"
                            />
                        </F>
                    </div>
                )}
            </div>

            <label className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-600 cursor-pointer select-none p-3 border border-slate-200 rounded-lg bg-slate-50">
                <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => {
                        setAgreed(e.target.checked);
                        if (errors._agreed) setErrors((er) => ({ ...er, _agreed: null }));
                    }}
                    data-testid="reg-input-agree"
                    className="mt-0.5 w-4 h-4 accent-[#1E3A8A] cursor-pointer shrink-0"
                />
                <span>
                    I have read and agree to the{" "}
                    <Link to="/policies/parent" target="_blank" className="text-[#1E3A8A] font-semibold hover:underline">Parent Policy</Link>
                    {" "}(including the Strict No-Bypass Clause),{" "}
                    <Link to="/policies/terms" target="_blank" className="text-[#1E3A8A] font-semibold hover:underline">Terms &amp; Conditions</Link>{" "}
                    and{" "}
                    <Link to="/policies/privacy" target="_blank" className="text-[#1E3A8A] font-semibold hover:underline">Privacy Policy</Link>.
                    I understand that all communication and payments must flow only through Anand Tutorials, and that any direct dealings with tutors outside the platform are strictly prohibited.
                </span>
            </label>
            <FieldError msg={errors._agreed} testid="reg-error-agree" />

            <button
                type="submit"
                disabled={submitting}
                data-testid="registration-submit-button"
                className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Submitting…" : "Book Free Demo Class"}
            </button>
        </form>
    );
}

const cls = (hasError) =>
    `w-full px-4 py-2.5 rounded-lg border bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition ${
        hasError ? "border-red-400 ring-1 ring-red-200" : "border-slate-200"
    }`;

const F = ({ label, children, error, hint }) => (
    <label className="block" data-field-error-anchor={error ? slug(label) : undefined}>
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">
            {label}{hint && <span className="ml-1 normal-case font-normal text-slate-400 tracking-normal">{hint}</span>}
        </span>
        {children}
        <FieldError msg={error} />
    </label>
);

function FieldError({ msg, testid }) {
    if (!msg) return null;
    return (
        <p
            className="mt-1 text-[12px] text-red-600 inline-flex items-center gap-1"
            data-testid={testid}
            role="alert"
        >
            <AlertCircle className="w-3 h-3 shrink-0" /> {msg}
        </p>
    );
}

function slug(label) {
    return (label || "")
        .toLowerCase()
        .replace(/[*()]/g, "")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-z_]/g, "");
}

function humanLabel(key) {
    const map = {
        name: "Student Name",
        parent_name: "Parent Name",
        email: "Email",
        phone: "Phone Number",
        mode: "Tuition Mode",
        country: "Country",
        city: "City",
        grade: "Grade",
        board: "Board",
        school_name: "School Name",
        subjects: "Subjects",
    };
    return map[key] || key;
}
